/**
 * Phase 101bl (15/02/2026) — Dialog validation refill MOMA-first
 *
 * Formulaire multi-lots FIFO (plus anciens en tête) pour compléter la traçabilité
 * HACCP d'un refill détecté côté MOMA. Chaque lot a : n° lot + DLC + quantité.
 * La somme des quantités doit égaler qty_detected (tolérance ±1).
 */
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Package, AlertCircle, PenLine } from "lucide-react";
import { api } from "../lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("fr-CH", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
}

export default function MomaRefillValidateDialog({ open, refill, onClose, onValidated }) {
  const [lots, setLots] = useState([]);
  const [availableLots, setAvailableLots] = useState([]); // Phase 101bn-bis
  const [loadingLots, setLoadingLots] = useState(false);
  const [totalQty, setTotalQty] = useState(0);   // pour mode stock simple
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [dismissReason, setDismissReason] = useState("");
  const [mode, setMode] = useState("validate"); // "validate" | "dismiss"

  const isBeverage = refill?.stock_managed === false;

  useEffect(() => {
    if (open && refill) {
      if (refill.stock_managed === false) {
        setTotalQty(refill.qty_detected);
        setLots([]);
      } else {
        // Init 1 lot vide, à sélectionner dans le dropdown
        setLots([{
          lot_id: "",
          lot_number: "",
          dlc: "",
          qty: refill.qty_detected,
          production_date: "",
          notes: "",
          fifo_position: 1,
          manual_mode: false,  // toggle "créer nouveau lot" (fallback)
        }]);
        setTotalQty(0);
      }
      setNotes("");
      setDismissReason("");
      setMode("validate");
    }
  }, [open, refill]);

  // Phase 101bn-bis — charger lots disponibles pour ce produit à Conthey
  useEffect(() => {
    if (!open || !refill || !refill.product_id_am || refill.stock_managed === false) {
      setAvailableLots([]);
      return;
    }
    setLoadingLots(true);
    api.get(`/moma-refills/available-lots/${refill.product_id_am}`)
      .then(({ data }) => setAvailableLots(data.lots || []))
      .catch((e) => {
        toast.error("Erreur chargement lots : " + (e?.response?.data?.detail || e.message));
        setAvailableLots([]);
      })
      .finally(() => setLoadingLots(false));
  }, [open, refill]);

  if (!refill) return null;

  const totalLotsQty = lots.reduce((s, l) => s + Number(l.qty || 0), 0);
  const qtyDelta = isBeverage
    ? Number(totalQty || 0) - refill.qty_detected
    : totalLotsQty - refill.qty_detected;
  // Phase 101cy — validation partielle autorisée (undershoot OK, overshoot
  // toujours interdit à +1 max). Le surplus détecté vient souvent de tx
  // Nayax historiques cumulées d'un produit retiré ; validation partielle +
  // bury automatique côté backend nettoie proprement.
  const totalSaisi = isBeverage ? Number(totalQty || 0) : totalLotsQty;
  const qtyMatch = totalSaisi > 0 && qtyDelta <= 1;
  const isPartial = qtyMatch && qtyDelta <= -1;

  // Vérif : chaque lot a soit un lot_id, soit un lot_number+dlc (manual mode)
  const lotsValid = lots.every(l => {
    if (Number(l.qty) <= 0) return false;
    if (l.manual_mode) return l.lot_number.trim() && l.dlc;
    return !!l.lot_id;
  });

  const canSubmit = mode === "validate"
    ? isBeverage
      ? qtyMatch && Number(totalQty) > 0
      : qtyMatch && lotsValid
    : dismissReason.trim().length > 0;

  const addLot = () => {
    setLots([...lots, {
      lot_id: "",
      lot_number: "",
      dlc: "",
      qty: 0,
      production_date: "",
      notes: "",
      fifo_position: lots.length + 1,
      manual_mode: false,
    }]);
  };

  const removeLot = (i) => {
    const next = lots.filter((_, idx) => idx !== i)
      .map((l, idx) => ({ ...l, fifo_position: idx + 1 }));
    setLots(next);
  };

  const updateLot = (i, field, value) => {
    const next = [...lots];
    next[i] = { ...next[i], [field]: value };
    setLots(next);
  };

  const submitValidate = async () => {
    setSubmitting(true);
    try {
      const payload = isBeverage
        ? { total_qty: Number(totalQty), notes: notes || null }
        : {
            lots: lots.map(l => ({
              lot_id: l.manual_mode ? null : (l.lot_id || null),
              lot_number: l.lot_number || "",
              dlc: l.dlc || null,
              qty: Number(l.qty),
              production_date: l.production_date || null,
              notes: l.notes || null,
              fifo_position: l.fifo_position,
            })),
            notes: notes || null,
          };
      const { data } = await api.post(`/moma-refills/${refill.id}/validate`, payload);
      const msg = isBeverage
        ? `Boisson validée — ${data.movements_created.length} mouvement(s) TRANSFER (lot perpétuel)`
        : `Refill validé — ${data.lots_created.length} lot(s) créé(s), ${data.movements_created.length} mouvement(s) TRANSFER`;
      toast.success(msg);
      const al = data.slot_alignment;
      if (al?.status === "removed") {
        toast.info(`Restes alignés sur Nayax (slot ${al.slot_code}) : ${al.removed_qty} ancienne(s) barquette(s) retirée(s) en écart d'inventaire${al.uncovered_qty > 0 ? `, ${al.uncovered_qty} non couverte(s)` : ""}`);
      } else if (al?.status === "shortage") {
        toast.warning(`Nayax compte ${al.shortage_qty} barquette(s) de plus qu'AgroManage sur le slot ${al.slot_code} — rien n'a été ajouté`);
      } else if (al?.status === "nayax_unavailable") {
        toast.warning("Nayax indisponible : les anciens restes de l'emplacement n'ont pas été vérifiés");
      }
      onValidated?.();
      onClose();
    } catch (e) {
      toast.error("Erreur validation : " + (e?.response?.data?.detail || e.message));
    } finally {
      setSubmitting(false);
    }
  };

  const submitDismiss = async () => {
    setSubmitting(true);
    try {
      await api.post(`/moma-refills/${refill.id}/dismiss`, { reason: dismissReason });
      toast.success("Refill rejeté (faux positif)");
      onValidated?.();
      onClose();
    } catch (e) {
      toast.error("Erreur : " + (e?.response?.data?.detail || e.message));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="moma-refill-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="w-5 h-5 text-[#1F4E3D]" />
            Compléter DLC — refill MOMA
          </DialogTitle>
          <DialogDescription>
            {refill.distributor_name} · slot <b>{refill.slot_code}</b> ·{" "}
            <b>{refill.product_name || "Produit inconnu"}</b>
          </DialogDescription>
        </DialogHeader>

        {/* Détails détection */}
        <div className="bg-[#FAFAF7] border border-[#E5E5E0] rounded-md p-3 text-xs space-y-1">
          <div className="flex justify-between">
            <span className="text-[#575A57]">Détecté :</span>
            <span className="font-mono">{fmtDate(refill.detected_at)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-[#575A57]">Stock avant → après :</span>
            <span className="font-mono">{refill.stock_before} → {refill.stock_after}</span>
          </div>
          {refill.sales_between > 0 && (
            <div className="flex justify-between">
              <span className="text-[#575A57]">Ventes entre snapshots :</span>
              <span className="font-mono">{refill.sales_between}</span>
            </div>
          )}
          <div className="flex justify-between pt-1 border-t border-[#E5E5E0]">
            <span className="text-[#1A1C1A] font-medium">Quantité à saisir :</span>
            <span className="font-mono font-medium text-[#1F4E3D]">{refill.qty_detected} barquette(s)</span>
          </div>
        </div>

        {/* Toggle mode */}
        <div className="flex gap-2 border-b border-[#E5E5E0] pb-2">
          <button
            className={`text-sm px-3 py-1.5 rounded ${mode === "validate" ? "bg-[#1F4E3D] text-white" : "bg-[#F4F4F0] text-[#575A57]"}`}
            onClick={() => setMode("validate")}
            data-testid="mode-validate-btn"
          >
            ✅ Valider avec lot(s)
          </button>
          <button
            className={`text-sm px-3 py-1.5 rounded ${mode === "dismiss" ? "bg-[#B93A25] text-white" : "bg-[#F4F4F0] text-[#575A57]"}`}
            onClick={() => setMode("dismiss")}
            data-testid="mode-dismiss-btn"
          >
            ❌ Faux positif (rejeter)
          </button>
        </div>

        {mode === "validate" ? (
          <>
            {isBeverage ? (
              // ==========================================================
              // MODE BOISSON / STOCK SIMPLE — pas de DLC, pas de multi-lots
              // ==========================================================
              <div className="space-y-3">
                <div className="bg-[#E8F2EC] border border-[#1F4E3D] rounded-md p-3 text-xs">
                  <div className="flex items-center gap-2 text-[#1F4E3D] font-medium mb-1">
                    <Package className="w-4 h-4" /> Produit stock simple (boisson/snack)
                  </div>
                  <p className="text-[#575A57]">
                    Pas de DLC ni de multi-lots. Ce refill sera consigné sur le
                    <b> lot perpétuel</b> du produit sur ce distributeur (créé automatiquement s&apos;il n&apos;existe pas).
                  </p>
                </div>

                <div className="max-w-xs">
                  <Label htmlFor="total-qty" className="text-sm">Quantité rechargée *</Label>
                  <Input
                    id="total-qty"
                    type="number"
                    min="0"
                    step="1"
                    value={totalQty}
                    onChange={(e) => setTotalQty(e.target.value)}
                    className="h-10 text-lg font-mono"
                    data-testid="beverage-qty-input"
                  />
                  <div className="text-[11px] text-[#8C8C88] mt-1">
                    Détecté : {refill.qty_detected} unité(s) — ajustez si nécessaire.
                  </div>
                </div>
              </div>
            ) : (
              // ==========================================================
              // MODE HACCP — Multi-lots FIFO avec DLC obligatoire
              // ==========================================================
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm text-[#1A1C1A]">
                    Lot(s) HACCP — FIFO (plus anciens en 1re ligne)
                  </Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addLot}
                    disabled={submitting}
                    className="border-[#1F4E3D] text-[#1F4E3D]"
                    data-testid="add-lot-btn"
                  >
                    <Plus className="w-3.5 h-3.5 mr-1" /> Ajouter un lot
                  </Button>
                </div>

                {lots.map((lot, i) => {
                  const selectedLot = availableLots.find(al => al.id === lot.lot_id);
                  const dlcWarning = selectedLot?.days_to_dlc !== null && selectedLot?.days_to_dlc !== undefined
                    ? (selectedLot.days_to_dlc < 0 ? "expired" : selectedLot.days_to_dlc < 3 ? "short" : null)
                    : null;

                  return (
                  <div key={i} className="border border-[#E5E5E0] rounded-md p-3 space-y-2 bg-white" data-testid={`lot-row-${i}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-[#8C6B2F]">
                        Lot #{lot.fifo_position} {i === 0 && "(front / plus ancien)"}
                      </span>
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => updateLot(i, "manual_mode", !lot.manual_mode)}
                          disabled={submitting}
                          className={`text-[10px] px-2 py-1 rounded flex items-center gap-1 border ${
                            lot.manual_mode
                              ? "bg-[#FBF4E9] border-[#E8D3A8] text-[#8C6B2F]"
                              : "bg-white border-[#E5E5E0] text-[#575A57] hover:border-[#A3A6A3]"
                          }`}
                          title="Basculer entre sélection de lot existant et création d'un nouveau lot"
                          data-testid={`toggle-manual-${i}`}
                        >
                          <PenLine className="w-3 h-3" />
                          {lot.manual_mode ? "Nouveau lot" : "Existant"}
                        </button>
                        {lots.length > 1 && (
                          <button
                            onClick={() => removeLot(i)}
                            disabled={submitting}
                            className="text-[#B93A25] hover:bg-[#FFEDEA] p-1 rounded"
                            data-testid={`remove-lot-${i}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </div>

                    {lot.manual_mode ? (
                      // === Mode CRÉATION (fallback rare) ===
                      <>
                        <div className="text-[10px] bg-[#FFF8EA] border border-[#E8D3A8] rounded p-1.5 text-[#8C6B2F]">
                          ⚠ Création d&apos;un nouveau lot en base (ex : réception non tracée). Habituellement, sélectionnez un lot existant.
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          <div>
                            <Label htmlFor={`lot-num-${i}`} className="text-xs">N° de lot *</Label>
                            <Input
                              id={`lot-num-${i}`}
                              value={lot.lot_number}
                              onChange={(e) => updateLot(i, "lot_number", e.target.value)}
                              placeholder="Ex : L20260215-1"
                              className="h-9"
                              data-testid={`lot-num-input-${i}`}
                            />
                          </div>
                          <div>
                            <Label htmlFor={`dlc-${i}`} className="text-xs">DLC *</Label>
                            <Input
                              id={`dlc-${i}`}
                              type="date"
                              value={lot.dlc}
                              onChange={(e) => updateLot(i, "dlc", e.target.value)}
                              className="h-9"
                              data-testid={`dlc-input-${i}`}
                            />
                          </div>
                          <div>
                            <Label htmlFor={`qty-${i}`} className="text-xs">Qté *</Label>
                            <Input
                              id={`qty-${i}`}
                              type="number"
                              min="0"
                              step="1"
                              value={lot.qty}
                              onChange={(e) => updateLot(i, "qty", e.target.value)}
                              className="h-9"
                              data-testid={`qty-input-${i}`}
                            />
                          </div>
                        </div>
                      </>
                    ) : (
                      // === Mode SÉLECTION LOT EXISTANT (Phase 101bn-bis) ===
                      <>
                        <div className="grid grid-cols-3 gap-2">
                          <div className="col-span-2">
                            <Label className="text-xs">Lot dispo (Conthey, LIFO) *</Label>
                            <Select value={lot.lot_id} onValueChange={(v) => updateLot(i, "lot_id", v)}>
                              <SelectTrigger className="h-9" data-testid={`lot-select-${i}`}>
                                <SelectValue placeholder={loadingLots ? "Chargement..." : availableLots.length === 0 ? "Aucun lot dispo" : "Sélectionner..."} />
                              </SelectTrigger>
                              <SelectContent>
                                {availableLots.length === 0 && !loadingLots && (
                                  <div className="px-2 py-2 text-xs text-[#8C8C88]">
                                    Aucun lot en stock à Conthey pour ce produit.
                                    Basculez en mode &quot;Nouveau lot&quot; pour créer.
                                  </div>
                                )}
                                {availableLots.map(al => {
                                  const dlcTxt = al.dlc ? new Date(al.dlc).toLocaleDateString("fr-CH", { day: "2-digit", month: "2-digit", year: "2-digit" }) : "—";
                                  const isExpired = al.days_to_dlc !== null && al.days_to_dlc < 0;
                                  const isShort = al.days_to_dlc !== null && al.days_to_dlc >= 0 && al.days_to_dlc < 3;
                                  return (
                                    <SelectItem key={al.id} value={al.id}>
                                      <span className={isExpired ? "text-[#B93A25]" : isShort ? "text-[#8C6B2F]" : ""}>
                                        {al.lot_number || "(sans n°)"} · DLC {dlcTxt} · {al.quantity} dispo
                                        {isExpired ? " ⚠ EXPIRÉE" : isShort ? ` ⚠ J-${al.days_to_dlc}` : ""}
                                      </span>
                                    </SelectItem>
                                  );
                                })}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label htmlFor={`qty-${i}`} className="text-xs">Qté *</Label>
                            <Input
                              id={`qty-${i}`}
                              type="number"
                              min="0"
                              step="1"
                              value={lot.qty}
                              onChange={(e) => updateLot(i, "qty", e.target.value)}
                              className="h-9"
                              data-testid={`qty-input-${i}`}
                            />
                          </div>
                        </div>
                        {selectedLot && (
                          <div className={`text-[11px] rounded p-2 border ${
                            dlcWarning === "expired"
                              ? "bg-[#FFEDEA] border-[#F0C8BF] text-[#B93A25]"
                              : dlcWarning === "short"
                                ? "bg-[#FFF8EA] border-[#E8D3A8] text-[#8C6B2F]"
                                : "bg-[#E8F2EC] border-[#C5DAC9] text-[#1F4E3D]"
                          }`}>
                            {dlcWarning === "expired" && "❌ DLC EXPIRÉE : "}
                            {dlcWarning === "short" && "⚠ DLC courte (< 3j) : "}
                            {!dlcWarning && "✅ "}
                            DLC {new Date(selectedLot.dlc).toLocaleDateString("fr-CH")}
                            {selectedLot.production_date && ` · Fab. ${new Date(selectedLot.production_date).toLocaleDateString("fr-CH")}`}
                            {selectedLot.days_to_dlc !== null && ` · J${selectedLot.days_to_dlc >= 0 ? "+" : ""}${selectedLot.days_to_dlc}`}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                  );
                })}
              </div>
            )}

            {/* Total qty warning */}
            {!qtyMatch && (
              <div className="flex items-start gap-2 text-xs bg-[#FFF8F5] border border-[#F0C8BF] rounded-md p-2" data-testid="qty-mismatch">
                <AlertCircle className="w-4 h-4 text-[#B93A25] flex-shrink-0 mt-0.5" />
                <div>
                  <b>Total {isBeverage ? "saisi" : "lots"} : {isBeverage ? Number(totalQty || 0) : totalLotsQty}</b>{" "}
                  {qtyDelta > 1
                    ? `> détecté (${refill.qty_detected}). Ajustez à la baisse.`
                    : `→ saisissez au moins 1 unité ou utilisez « Faux positif ».`}
                </div>
              </div>
            )}
            {isPartial && (
              <div className="flex items-start gap-2 text-xs bg-[#FFF7DB] border border-[#E5DBC1] rounded-md p-2" data-testid="qty-partial">
                <AlertCircle className="w-4 h-4 text-[#8C6B2F] flex-shrink-0 mt-0.5" />
                <div>
                  <b>Validation partielle</b> — vous confirmez <b>{totalSaisi}</b> unité(s) sur <b>{refill.qty_detected}</b> détectée(s).
                  <div className="text-[#575A57] mt-0.5">
                    Le reste ({refill.qty_detected - totalSaisi}) sera automatiquement enterré comme faux positif — plus jamais reproposé.
                  </div>
                </div>
              </div>
            )}

            <div>
              <Label htmlFor="notes" className="text-xs">Notes (optionnel)</Label>
              <Textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                placeholder="Contexte, remarques..."
                data-testid="notes-input"
              />
            </div>
          </>
        ) : (
          <div className="space-y-2">
            <Label htmlFor="dismiss-reason" className="text-sm">Motif du rejet *</Label>
            <Textarea
              id="dismiss-reason"
              value={dismissReason}
              onChange={(e) => setDismissReason(e.target.value)}
              rows={3}
              placeholder="Ex : rechargement test / erreur MOMA / déjà validé via tournée refill..."
              data-testid="dismiss-reason-input"
            />
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting} data-testid="cancel-btn">
            Annuler
          </Button>
          {mode === "validate" ? (
            <Button
              onClick={submitValidate}
              disabled={!canSubmit || submitting}
              className="bg-[#1F4E3D] hover:bg-[#173B2E] text-white"
              data-testid="submit-validate-btn"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              {isBeverage
                ? `Valider (lot perpétuel +${Number(totalQty || 0)})`
                : `Valider et créer ${lots.length} lot(s)`}
            </Button>
          ) : (
            <Button
              onClick={submitDismiss}
              disabled={!canSubmit || submitting}
              className="bg-[#B93A25] hover:bg-[#9A2E1E] text-white"
              data-testid="submit-dismiss-btn"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Rejeter comme faux positif
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
